/**
 * Who may choose shops, and which shops an account is entitled to.
 *
 * The live API sends the effective `store_scope` / `can_select_shops` from the
 * backend (`scoped_store_ids` rule, D-42). The role-based branches remain only
 * as a fallback for older fixtures: company-wide roles see every shop; an
 * explicit district scope wins; otherwise the person's own `store_id` is their
 * scope. An account with neither gets nothing — never the whole company by
 * accident.
 */
import type { StoreOption } from '../api/auth'
import type { AuthUser } from '../types/identity'

const COMPANY_WIDE_ROLES = new Set<string>(['operations_manager', 'system_admin'])

/**
 * Who may choose shops (dictated 2026-10-17): **general manager and below get no
 * shop selector** — they have one store and a date range is all they need.
 * District manager and above may pick one shop, several, or all of them.
 */
export function canSelectShops(user: AuthUser | null): boolean {
  if (!user) return false
  if (typeof user.can_select_shops === 'boolean') return user.can_select_shops
  // Fallback for older test fixtures; the live API always returns the field.
  if (user.is_platform_admin) return true
  return ['district_manager', 'operations_manager', 'system_admin'].includes(user.role)
}

/** The shops this account may look at, in the catalogue's order. */
export function entitledStores(
  user: AuthUser | null,
  stores: StoreOption[],
): StoreOption[] {
  if (!user) return []
  // The backend's effective scope is authoritative: null = every shop.
  if (user.store_scope !== undefined) {
    if (user.store_scope === null) return stores
    const scope = new Set(user.store_scope)
    return stores.filter((store) => scope.has(store.id))
  }
  // Fallback for older fixtures.
  if (user.is_platform_admin || COMPANY_WIDE_ROLES.has(user.role)) return stores
  if (user.scope_store_ids && user.scope_store_ids.length > 0) {
    const scope = new Set(user.scope_store_ids)
    return stores.filter((store) => scope.has(store.id))
  }
  if (user.store_id !== null) {
    return stores.filter((store) => store.id === user.store_id)
  }
  return []
}
